/* eslint-disable no-restricted-globals */
import { parseISO } from 'date-fns/parseISO';
import { toDate } from 'date-fns/toDate';
import jc from 'json-cycle';
import { GLOBAL, getViewportSize } from 'saddlebag-browser';
import { v4 as uuidv4 } from 'uuid';

import { PRECISION, DCS } from './enums';

const isDevEnv = () => {
  const $window = GLOBAL.getWindow();
  return $window.location.hostname === 'localhost';
};

/**
 * Returns the reconciled referrer URL.
 *
 * In SPAs, document.referrer does not update on soft navigations — it only
 * reflects the last hard navigation. An SPA should maintain a virtualReferrer that maintains logic for soft
 * navigations, refreshes, and back navigations.
 *
 * This function reconciles the two: virtualReferrer if set, otherwise
 * document.referrer.
 *
 * @param {string} [virtualReferrer] - App-maintained referrer that mimics MPA
 *   document.referrer across soft navigations.
 * @returns {string} The reconciled referrer URL.
 */
const getReconciledReferrer = (virtualReferrer) => {
  const $document = GLOBAL.getDocument();
  return virtualReferrer || $document.referrer;
};

const safeTrim = (str) => `${str || ''}`.trim();

const precision = (num) => {
  if (!isFinite(num)) {
    return 0;
  }

  let retValue = 0;
  let exponent = 1;
  const maxRecursion = 10 ** 20;

  while (
    Math.round(num * exponent) / exponent !== num &&
    exponent <= maxRecursion
  ) {
    if (exponent >= maxRecursion) {
      return null;
    }
    exponent *= 10;
    retValue++; // eslint-disable-line no-plusplus
  }

  return retValue;
};

const floatToPrecisionInt = (fpVal) => {
  let fp = null;
  const value = !isNaN(+fpVal) ? parseFloat(fpVal) : null;
  if (value && typeof value === 'number' && !isNaN(value)) {
    fp = {
      precision: precision(value),
      amount: parseInt(`${value}`.replace('.', ''), 10),
    };
    if (fp.amount.toString().length >= 10) {
      const remove = fp.amount.toString().length - 10;
      fp.precision -= remove;
      fp.amount = parseInt(fp.amount.toString().substring(0, 10), 10);
    }
    if (2147483647 - fp.amount < 0) {
      fp.precision -= 1;
      fp.amount = parseInt(fp.amount.toString().substring(0, 9), 10);
    }
  }
  return fp;
};

const parseAssociateId = (associateId) => {
  const associateIdPattern = /([a-z]{3}[_][a-z]{3}[_][0-9]{5}[_][0-9]{5})/i;
  if (associateId && associateIdPattern.test(associateId)) {
    return associateId.match(associateIdPattern)[0].toUpperCase();
  }
  return 'DIR_ECT_00000_00000';
};

const isEmpty = (value) => {
  if (value === undefined || value === null || value === '') {
    return true;
  }

  // Check for empty object or array
  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return true;
  }

  // Check for NaN
  if (typeof value === 'number' && value !== +value) {
    return true;
  }
  return false;
};

const pruneEmpty = (obj) => {
  if (!obj) {
    return obj;
  }
  let deepClone = JSON.parse(jc.stringify(obj));
  Object.keys(deepClone).forEach((key) => {
    const value = deepClone[key];
    if (
      (value &&
        (value.constructor === Object || value.constructor === Array)) ||
      typeof value === 'function'
    ) {
      deepClone[key] = pruneEmpty(value);
    }
    if (isEmpty(value)) {
      delete deepClone[key];
    }
  });

  if (Array.isArray(deepClone)) {
    deepClone = deepClone.filter(Boolean);
  }
  return deepClone;
};

// Date.prototype.getTimezoneOffset returns a number representing the difference, in minutes,
// between the date as evaluated in the UTC time zone and as evaluated in the local time zone
//
// more info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset
const getTimezoneOffsets = (dtIn, january, july) => {
  let jan = january;
  let jul = july;
  const currDate = dtIn || new Date();
  const thisTZOffset = currDate.getTimezoneOffset();

  const janTimeZoneOffset = jan && jan.getTimezoneOffset();
  const julTimeZoneOffset = jul && jul.getTimezoneOffset();
  jan =
    janTimeZoneOffset ||
    parseISO(`${currDate.getFullYear()}-01-01`).getTimezoneOffset();
  jul =
    julTimeZoneOffset ||
    parseISO(`${currDate.getFullYear()}-07-01`).getTimezoneOffset();

  return {
    thisTZOffset,
    jan,
    jul,
  };
};

const getDateOffsets = (dtIn, january, july) => {
  let dst = null;
  let dstOffset = 0;

  const { jan, jul, thisTZOffset } = getTimezoneOffsets(dtIn, january, july);
  const hasDST = jul !== jan; // difference in timezone offset of Jan vs Jul indicates DST

  if (jan > jul) {
    dst = jul;
    dstOffset = jan - jul;
  } else if (jan < jul) {
    dst = jan;
    dstOffset = jul - jan;
  }

  if (hasDST && dst != null && dst === thisTZOffset) {
    return {
      DSTOffset: dstOffset,
      TZOffset: thisTZOffset * -1 - dstOffset,
    };
  }

  return {
    DSTOffset: 0,
    TZOffset: thisTZOffset * -1 || 0,
  };
};

const check = {
  int(num) {
    if (!isNaN(+num)) {
      return parseInt(num, 10);
    }
    return null;
  },
  isFloat(num) {
    if (!isNaN(+num)) {
      return parseFloat(num);
    }
    return null;
  },
  isUnknown(val) {
    if (val && val.toString().toLowerCase() === 'unknown') {
      return null;
    }
    return val;
  },
  isDate(dtsString, dateFormat) {
    if (!dtsString) {
      return null;
    }
    if (dtsString instanceof Date) {
      return dtsString;
    }
    if (typeof dtsString === 'number') {
      return toDate(dtsString);
    }
    if (typeof dtsString === 'string' && dateFormat === 'YYYYMMDDHHmmss') {
      try {
        return parseISO(
          `${dtsString.slice(0, 4)}-${dtsString.slice(4, 6)}-${dtsString.slice(
            6,
            8,
          )}T${dtsString.slice(8, 10)}:${dtsString.slice(
            10,
            12,
          )}:${dtsString.slice(12, 14)}`,
        );
      } catch (error) {
        return null;
      }
    }
    if (typeof dtsString === 'string') {
      let utcDate;
      const dtsplit = dtsString.split(/[ZT:\\.\\//-]/);
      try {
        utcDate = toDate(
          Date.UTC(
            dtsplit[0],
            dtsplit[1] - 1 || 0,
            dtsplit[2] || 1,
            dtsplit[3] || null,
            dtsplit[4] || null,
            dtsplit[5] || null,
          ),
        );
      } catch (error1) {
        return null;
      }
      if (utcDate instanceof Date && !isNaN(utcDate.valueOf())) {
        return utcDate;
      }
      return null;
    }
    return null;
  },
  parseBool(bool) {
    if (typeof bool === 'boolean') {
      return bool;
    }
    if (typeof bool === 'number') {
      return !!bool;
    }
    if (typeof bool === 'string') {
      switch (safeTrim(bool.toLowerCase())) {
        case 'true':
          return true;
        case 'yes':
          return true;
        case '1':
          return true;
        case 'false':
          return false;
        case 'no':
          return false;
        case '0':
          return false;
        default:
          return null;
      }
    }
    return null;
  },
  isBool(bool) {
    return typeof bool === 'boolean';
  },
};

const getCommonsDateTime = (
  dtsStr,
  includeOffsets,
  precisionToUse,
  dateFormat,
) => {
  let prec = precisionToUse || null;
  let dtsString = dtsStr;
  if (
    typeof dtsStr === 'string' &&
    dtsStr &&
    dtsStr.length === 4 &&
    dtsStr.substring(2, 4) <= 12
  ) {
    dtsString = `20${dtsStr}`;
  }
  const dts = check.isDate(dtsString, dateFormat);
  if (dts && typeof dtsString === 'string' && !prec) {
    prec = (() => {
      switch (dtsString.length) {
        case 4:
          return 'YEAR';
        case 6:
          return 'MONTH';
        case 7:
          return 'MONTH';
        case 8:
          return 'DAY';
        case 10:
          return 'DAY';
        default:
          return 'MILLI';
      }
    })();
  }
  if (dts && dts instanceof Date && !prec) {
    prec = 'MILLI';
  }
  let dtsObj = {};
  if (dts) {
    dtsObj = {
      unix_time_millis: dts.getTime(),
      date_time_kind: PRECISION[prec],
    };
  }
  if (dts && dts instanceof Date && includeOffsets) {
    const offsets = getDateOffsets(dts);
    dtsObj.timezone_offset_mins = offsets.TZOffset || 0;
    dtsObj.daylight_savings_offset_mins = offsets.DSTOffset || 0;
  }
  if (!dtsObj.unix_time_millis && !dtsObj.date_time_kind) {
    return null;
  }
  return pruneEmpty(dtsObj);
};

const getAssociateId = (assId) =>
  check.isUnknown(assId) ? parseAssociateId(assId) : null;

const getServiceFingerprint = (details) => {
  // eslint-disable-next-line camelcase
  const { identifier, nodeName, version, version_identifier } = details;
  const sub =
    nodeName && nodeName.length >= 8 ? nodeName.substring(5, 8) : null;
  const { dc = DCS[sub] } = details;
  return pruneEmpty({
    data_center: dc,
    name: nodeName,
    version_tag: version,
    // eslint-disable-next-line camelcase
    version_identifier,
    identifier,
  });
};

const getUniqueGuid = () => uuidv4();

const getTravellerIdentity = (utid, authState) => {
  let markZero = false;
  if ([2, 1, 0].indexOf(Number(authState)) === -1) {
    markZero = true;
  }
  const ti = {
    utid,
    user_status: markZero ? '0' : authState.toString(),
    authentication_status: check.int(authState),
  };
  return pruneEmpty(ti);
};

const getTravellerCultureSettings = (culture = {}) =>
  pruneEmpty({
    country: culture.market,
    currency: culture.currency,
    locale: culture.locale,
  });

const getScreen = () => {
  const $window = GLOBAL.getWindow();
  const viewport = getViewportSize();
  const sumOfSquares =
    viewport.width * viewport.width + viewport.height * viewport.height;
  const diag = Math.round((Math.sqrt(sumOfSquares) / 72) * 10) / 10;
  const screen = $window.screen || {};
  return {
    usable_width: viewport.width,
    usable_height: viewport.height,
    display_width: screen.width,
    display_height: screen.height,
    display_ppi: screen.pixelDepth,
    diag_in_inches: diag,
    device_pixel_ratio: $window.device_pixel_ratio || null,
  };
};

const getClientDetails = (device = {}, virtualReferrer) => {
  const screen = getScreen();
  const nav = window.navigator || {};

  const cd = {
    user_agent: nav.userAgent,
    vendor_name: nav.vendor,
    browser_name: check.isUnknown(device.browserName) || nav.appName,
    browser_version: check.isUnknown(device.browserVersion) || nav.appVersion,
    is_browser: check.isUnknown(device.desktop),
    display_height: check.int(screen.display_height),
    display_width: check.int(screen.display_width),
    display_ppi: check.int(screen.display_ppi),
    usable_display_width: check.int(screen.usable_width),
    usable_display_height: check.int(screen.usable_height),
    diagonal_screen_size_int: floatToPrecisionInt(screen.diag_in_inches),
    device_pixel_ratio_int:
      floatToPrecisionInt(screen.device_pixel_ratio) || null,
    is_mobilephone: check.isUnknown(device.mobile),
    is_tablet: check.isUnknown(device.tablet),
    os_version: check.isUnknown(device.osVersion),
  };
  cd.referrer_url = getReconciledReferrer(virtualReferrer);
  return pruneEmpty(cd);
};

const getEventHeader = (initialOptions, err) => {
  const options = initialOptions || {};
  const {
    authState,
    culture,
    device,
    featureTests,
    guid,
    utid,
    virtualReferrer,
  } = options;
  const eh = {};
  try {
    eh.produced_by = 2;
    eh.traveller_identity = getTravellerIdentity(utid, authState);
    eh.client_details = getClientDetails(device, virtualReferrer);
    eh.traveller_culture_settings = getTravellerCultureSettings(culture);
    eh.ab_tests = pruneEmpty(featureTests || null);
    eh.created_on = getCommonsDateTime(new Date(), true);
    eh.associate_id = null;
    eh.guid = guid || getUniqueGuid();
  } catch (e) {
    if (!err) {
      throw e;
    }
  }
  return eh;
};

export {
  getReconciledReferrer,
  getScreen,
  getDateOffsets,
  floatToPrecisionInt,
  isDevEnv,
  parseAssociateId,
  pruneEmpty,
  getUniqueGuid,
  getEventHeader,
  getAssociateId,
  getTravellerIdentity,
  getTravellerCultureSettings,
  getServiceFingerprint,
  getCommonsDateTime,
  safeTrim,
  check,
};
